import { Box, Group, Stack, Text } from "@mantine/core";
import { IconCheck } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";

import { DateFilter, PriorityFilter, TypeFilter } from "./useTodoFilters";

interface FilterItemProps {
  label: string;
  isActive: boolean;
  onClick: () => void;
  showCheckmark?: boolean;
}

const FilterItem = ({ label, isActive, onClick, showCheckmark = false }: FilterItemProps) => (
  <Box
    px="md"
    py="sm"
    style={{
      cursor: "pointer",
      borderRadius: "var(--mantine-radius-sm)",
      backgroundColor: isActive ? "var(--mantine-color-gray-1)" : "transparent",
    }}
    onClick={onClick}
  >
    {showCheckmark ? (
      <Group gap="xs">
        <IconCheck size={14} color="gray" />
        <Text fz={14} fw={isActive ? 600 : 400}>
          {label}
        </Text>
      </Group>
    ) : (
      <Text fz={14} fw={isActive ? 600 : 400}>
        {label}
      </Text>
    )}
  </Box>
);

interface FilterSidebarProps {
  dateFilter: DateFilter;
  setDateFilter: (filter: DateFilter) => void;
  typeFilter: TypeFilter;
  setTypeFilter: (filter: TypeFilter) => void;
  priorityFilter: PriorityFilter;
  setPriorityFilter: (filter: PriorityFilter) => void;
  archiveFilter: boolean;
  setArchiveFilter: (filter: boolean) => void;
}

const FilterSidebar = ({
  dateFilter,
  setDateFilter,
  typeFilter,
  setTypeFilter,
  priorityFilter,
  setPriorityFilter,
  archiveFilter,
  setArchiveFilter,
}: FilterSidebarProps) => {
  const { t } = useTranslation();

  return (
    <Box
      flex={0.5}
      p="lg"
      style={{
        borderRight: "1px solid var(--mantine-color-gray-3)",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
      }}
    >
      <Box>
        {/* Date filters */}
        <Stack gap={0}>
          <FilterItem
            label={t("todoList.filters.date.today")}
            isActive={dateFilter === "today"}
            onClick={() => setDateFilter("today")}
          />
          <FilterItem
            label={t("todoList.filters.date.nextSevenDays")}
            isActive={dateFilter === "week"}
            onClick={() => setDateFilter("week")}
          />
          <FilterItem
            label={t("todoList.filters.date.absence")}
            isActive={dateFilter === "absence"}
            onClick={() => setDateFilter("absence")}
          />
          <FilterItem
            label={t("todoList.filters.date.all")}
            isActive={dateFilter === "all"}
            onClick={() => setDateFilter("all")}
          />
        </Stack>

        {/* Type filters */}
        <Text fz={13} c="dimmed" mt="xl" mb="xs">
          {t("todoList.filters.typeTitle")}
        </Text>
        <Stack gap={0}>
          <FilterItem
            label={t("todoList.filters.allTypes")}
            isActive={typeFilter === "all"}
            onClick={() => setTypeFilter("all")}
          />
          <FilterItem
            label={t("todoList.filters.types.call")}
            isActive={typeFilter === "CALL"}
            onClick={() => setTypeFilter("CALL")}
          />
          <FilterItem
            label={t("todoList.filters.types.meeting")}
            isActive={typeFilter === "MEETING"}
            onClick={() => setTypeFilter("MEETING")}
          />
          <FilterItem
            label={t("todoList.filters.types.internalTask")}
            isActive={typeFilter === "INTERNAL_TASK"}
            onClick={() => setTypeFilter("INTERNAL_TASK")}
          />
        </Stack>

        {/* Priority filters */}
        <Text fz={13} c="dimmed" mt="xl" mb="xs">
          {t("todoList.filters.priorityTitle")}
        </Text>
        <Stack gap={0}>
          <FilterItem
            label={t("todoList.priority.urgent")}
            isActive={priorityFilter === "urgent"}
            onClick={() => setPriorityFilter(priorityFilter === "urgent" ? "all" : "urgent")}
            showCheckmark
          />
          <FilterItem
            label={t("todoList.priority.important")}
            isActive={priorityFilter === "important"}
            onClick={() => setPriorityFilter(priorityFilter === "important" ? "all" : "important")}
            showCheckmark
          />
        </Stack>
      </Box>

      {/* Archive filter - positioned at bottom */}
      <Box>
        <FilterItem
          label={t("todoList.filters.archive")}
          isActive={archiveFilter}
          onClick={() => setArchiveFilter(!archiveFilter)}
        />
      </Box>
    </Box>
  );
};

export default FilterSidebar;
